/**
 * Returns a Promise that resolves when the given HTML element enters the viewport.
 *
 * @param {HTMLElement | Element} htmlElement - The HTML element to observe.
 * @param {number} [threshold=0.7] - The threshold for intersection.
 * @param {string} [rootMargin="0px"] - The margin around the viewport.
 * @return {Promise<IntersectionObserverEntry | null>} A Promise that resolves with the intersection entry or null if IntersectionObserver is not supported.
 */
export function onViewportEnter<T extends HTMLElement | Element>(htmlElement: T, threshold = 0.7, rootMargin = "0px") {
    return new Promise<IntersectionObserverEntry | null>((res) => {
        if (typeof IntersectionObserver != "undefined") {
            const options: IntersectionObserverInit = {
                threshold: threshold,
                rootMargin,
            };

            const observer = new IntersectionObserver((entries, observer) => {
                entries
                    .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= threshold)
                    .forEach((entry) => {
                        res(entry);
                        observer.unobserve(entry.target);
                    });
            }, options);

            observer.observe(htmlElement as HTMLElement);
        } else {
            res(null);
        }
    });
}
